/**
 * Hook for circular flick controller input.
 * Vector = center of circle → current drag position.
 * Circle boundary = max impulse magnitude.
 */

import { useCallback, useRef, useState } from "react";

const MAX_DRAG_DISTANCE = 2.0;
const POWER_SCALE = 3.0;

export interface ControllerDragState {
  readonly isDragging: boolean;
  /** Board-space vector from center (clamped to MAX_DRAG_DISTANCE). */
  readonly dx: number;
  readonly dy: number;
  /** Pixel offset from center (unclamped). */
  readonly rawPixelDx: number;
  readonly rawPixelDy: number;
}

interface FlickResult {
  readonly impulseX: number;
  readonly impulseY: number;
}

export function useFlickController(
  pixelsPerBoardUnit: number,
  onFlick: (result: FlickResult) => void,
  onDragStateChange: (state: ControllerDragState | null) => void,
) {
  const [dragState, setDragState] = useState<ControllerDragState | null>(null);

  const centerRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const dragStateRef = useRef<ControllerDragState | null>(null);
  const onFlickRef = useRef(onFlick);
  onFlickRef.current = onFlick;
  const onDragStateChangeRef = useRef(onDragStateChange);
  onDragStateChangeRef.current = onDragStateChange;
  const pixelsRef = useRef(pixelsPerBoardUnit);
  pixelsRef.current = pixelsPerBoardUnit;

  const handlePointerDown = useCallback(
    (pixelX: number, pixelY: number, centerX: number, centerY: number) => {
      centerRef.current = { x: centerX, y: centerY };

      const rawPixelDx = pixelX - centerX;
      const rawPixelDy = pixelY - centerY;
      const boardDx = rawPixelDx / pixelsRef.current;
      const boardDy = -rawPixelDy / pixelsRef.current;

      const distance = Math.sqrt(boardDx * boardDx + boardDy * boardDy);
      const clampScale =
        distance > MAX_DRAG_DISTANCE ? MAX_DRAG_DISTANCE / distance : 1;

      const initial: ControllerDragState = {
        isDragging: true,
        dx: boardDx * clampScale,
        dy: boardDy * clampScale,
        rawPixelDx,
        rawPixelDy,
      };
      dragStateRef.current = initial;
      setDragState(initial);
      onDragStateChangeRef.current(initial);
    },
    [],
  );

  const handlePointerMove = useCallback((pixelX: number, pixelY: number) => {
    const center = centerRef.current;
    const rawPixelDx = pixelX - center.x;
    const rawPixelDy = pixelY - center.y;
    const boardDx = rawPixelDx / pixelsRef.current;
    const boardDy = -rawPixelDy / pixelsRef.current;

    const distance = Math.sqrt(boardDx * boardDx + boardDy * boardDy);
    const clampScale =
      distance > MAX_DRAG_DISTANCE ? MAX_DRAG_DISTANCE / distance : 1;

    const next: ControllerDragState = {
      isDragging: true,
      dx: boardDx * clampScale,
      dy: boardDy * clampScale,
      rawPixelDx,
      rawPixelDy,
    };
    dragStateRef.current = next;
    setDragState(next);
    onDragStateChangeRef.current(next);
  }, []);

  const handlePointerUp = useCallback(() => {
    const prev = dragStateRef.current;
    if (!prev?.isDragging) {
      dragStateRef.current = null;
      setDragState(null);
      onDragStateChangeRef.current(null);
      return;
    }

    const impulseX = -prev.dx * POWER_SCALE;
    const impulseY = -prev.dy * POWER_SCALE;

    dragStateRef.current = null;
    setDragState(null);
    onFlickRef.current({ impulseX, impulseY });
    onDragStateChangeRef.current(null);
  }, []);

  return {
    dragState,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
  };
}

export { MAX_DRAG_DISTANCE };
